import { Outlet } from 'react-router-dom';
import { TopNavBar } from '@/components/layout/TopNavBar';
import { ThemeProvider } from '@/app/ThemeProvider';
import type { PortalRole } from '@/lib/constants';

interface PortalLayoutProps {
  role: PortalRole;
}

/**
 * The shell for the four authenticated role portals.
 *
 * theme-customization A3 adds two things:
 *
 *   · THE PROVIDER BOUNDARY. `ThemeProvider` mounts HERE — below `AuthGuard`/`RoleGuard`
 *     (`app/router.tsx` wraps every `PortalLayout` in both) and not in global `Providers`, so
 *     the palette is fetched only for an authenticated portal user and the public routes stay
 *     theme-independent. It gates both the navigation and the `Outlet`: nothing of the portal
 *     paints until a validated, owner-matched palette is applied.
 *   · THE PRINT WRAPPER. `portal-print-surface` is the hook `globals.css` uses to put the
 *     COMPLETE stock-light palette (plus an explicit foreground and a stock-white sheet) on this
 *     subtree under print media. The runtime writes its variables as inline custom properties on
 *     `documentElement`, and inline properties inherit; re-declaring the whole set on this
 *     wrapper is what stops a dark or heavily customised palette from being what comes out of
 *     the printer. It is on the ROOT element so the nav is covered as well as the content, and
 *     `main#main-content` stays a descendant — `intel-report/print.css` still reaches it by name.
 */
export function PortalLayout({ role }: PortalLayoutProps) {
  return (
    <div className="portal-print-surface min-h-screen bg-background">
      <ThemeProvider>
        {/* Skip-to-content link for keyboard accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-white focus:text-content-primary focus:shadow-lg focus:rounded-md focus:top-2 focus:left-2 focus:text-sm focus:font-medium"
        >
          Skip to main content
        </a>
        <TopNavBar role={role} />
        <main id="main-content" className="flex-1 p-5 px-6 mt-0" tabIndex={-1}>
          <div className="max-w-screen-2xl mx-auto">
            <Outlet />
          </div>
        </main>
      </ThemeProvider>
    </div>
  );
}
